import moment from 'moment';
import CommonUtils from 'utilities/common-utils';
import { CustomViewsType, FilterType, ServiceType } from 'utilities/enum-utils';

/**
 * Returns default booking views with date filters computed at call time.
 * Use this instead of a static array so dates don't become stale (e.g. after midnight).
 */
export function getDefaultBookingViews() {
  return [
  {
    customViewId: 'ALL',
    name: 'All Bookings',
    isPinned: true,
    isDefault: true,
    viewType: CustomViewsType.EVERYONE,
    filterValue: [
      {
        filter: FilterType.DATE_RANGE,
        values: [moment().startOf('isoWeek'), moment().endOf('isoWeek')],
        selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
          moment().startOf('isoWeek'),
          moment().endOf('isoWeek'),
        ]),
      },
      { filter: 'sort', values: [['startDateTime', 'asc']] },
      {
        filter: FilterType.CUSTOMER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOMER).fullSelectionName,
      },
      {
        filter: FilterType.WORKER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.WORKER).fullSelectionName,
      },
      {
        filter: FilterType.SHIFT_SLOT_STATUS,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.SHIFT_SLOT_STATUS).fullSelectionName,
      },
      {
        filter: FilterType.SERVICE_TYPE,
        values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
        selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
      },
    ],
  },
  {
    customViewId: 'NO_WORKER_CONFIRMED',
    name: 'No team members confirmed',
    isPinned: true,
    isDefault: true,
    viewType: CustomViewsType.EVERYONE,
    filterValue: [
      {
        filter: FilterType.DATE_RANGE,
        values: [moment().startOf('day'), moment().add(30, 'days').endOf('day')],
        selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
          moment().startOf('day'),
          moment().add(30, 'days').endOf('day'),
        ]),
      },
      {
        filter: FilterType.CUSTOMER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOMER).fullSelectionName,
      },
      {
        filter: FilterType.WORKER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.WORKER).fullSelectionName,
      },
      {
        filter: FilterType.SHIFT_SLOT_STATUS,
        values: ['UNASSIGNED', 'PENDING'],
        selectionLabel: CommonUtils.getFilterText(FilterType.SHIFT_SLOT_STATUS, ['UNASSIGNED', 'PENDING', 'PUBLISHED']),
      },
      {
        filter: FilterType.BOOKING_STATUS,
        values: ['ACCEPTED', 'CONFIRMED', 'PENDING'],
        selectionLabel: CommonUtils.getFilterText(FilterType.BOOKING_STATUS, ['ACCEPTED', 'CONFIRMED', 'PENDING']),
      },
      {
        filter: FilterType.SERVICE_TYPE,
        values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
        selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
      },
      { filter: 'sort', values: [['startDateTime', 'asc']] },
    ],
  },
  {
    customViewId: 'OVERDUE',
    name: 'Overdue',
    isPinned: true,
    isDefault: true,
    viewType: CustomViewsType.EVERYONE,
    filterValue: [
      {
        filter: FilterType.DATE_RANGE,
        values: [moment().add(-1, 'year'), moment().startOf('day')],
        selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
          moment().add(-1, 'year'),
          moment().startOf('day'),
        ]),
      },
      {
        filter: FilterType.CUSTOMER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOMER).fullSelectionName,
      },
      {
        filter: FilterType.WORKER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.WORKER).fullSelectionName,
      },
      {
        filter: FilterType.BOOKING_STATUS,
        values: ['PENDING', 'ACCEPTED', 'CONFIRMED'],
        selectionLabel: CommonUtils.getFilterText(FilterType.BOOKING_STATUS, ['PENDING', 'ACCEPTED', 'CONFIRMED']),
      },
      {
        filter: FilterType.SERVICE_TYPE,
        values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
        selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
      },
      { filter: 'sort', values: [['startDateTime', 'asc']] },
    ],
  },
  {
    customViewId: 'REQUIRES_ACTION',
    name: 'Requires action',
    isPinned: true,
    isDefault: true,
    viewType: CustomViewsType.EVERYONE,
    filterValue: [
      {
        filter: FilterType.DATE_RANGE,
        values: [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
        selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
          moment().subtract(30, 'days').startOf('day'),
          moment().endOf('day'),
        ]),
      },
      {
        filter: FilterType.CUSTOMER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOMER).fullSelectionName,
      },
      {
        filter: FilterType.WORKER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.WORKER).fullSelectionName,
      },
      {
        filter: FilterType.PAYMENT_STATUS,
        values: ['REQUIRES_APPROVAL'],
        selectionLabel: 'Requires approval',
      },
      {
        filter: FilterType.APPROVAL_READINESS,
        values: ['NEEDS_ACTION'],
        selectionLabel: 'Requires action',
      },
      {
        filter: FilterType.SERVICE_TYPE,
        values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
        selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
      },
      { filter: 'sort', values: [['startDateTime', 'asc']] },
    ],
    },
    {
      customViewId: 'REQUIRED_APPROVAL',
      name: 'Requires approval',
      isPinned: true,
      isDefault: true,
      viewType: CustomViewsType.EVERYONE,
      filterValue: [
        {
          filter: FilterType.DATE_RANGE,
          values: [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
          selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, [
            moment().subtract(30, 'days').startOf('day'),
            moment().endOf('day'),
          ]),
        },
        {
          filter: FilterType.CUSTOMER,
          values: [],
          selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOMER).fullSelectionName,
        },
        {
          filter: FilterType.WORKER,
          values: [],
          selectionLabel: CommonUtils.getFilterSettings(FilterType.WORKER).fullSelectionName,
        },
        {
          filter: FilterType.PAYMENT_STATUS,
          values: ['REQUIRES_APPROVAL'],
          selectionLabel: 'Requires approval',
        },
        {
          filter: FilterType.APPROVAL_READINESS,
          values: ['READY'],
          selectionLabel: 'Ready to approve',
        },
        {
          filter: FilterType.SERVICE_TYPE,
          values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
          selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
        },
        { filter: 'sort', values: [['startDateTime', 'asc']] },
      ],
    },
  {
    customViewId: 'PUBLISHED_RESPONSES',
    name: 'Published Responses',
    isPinned: true,
    isDefault: true,
    viewType: CustomViewsType.EVERYONE,
    filterValue: [
      {
        // Only published shifts with at least one submitted application. Backend joins
        // PublishShifts (PUBLISHING) + PublishShiftApplications and the badge count becomes total applicants.
        filter: FilterType.PUBLISH_APPLICATION_STATUS,
        values: ['APPLIED'],
        selectionLabel: 'Applied',
      },
      {
        // Restrict to bookings whose slot is still actively publishing. The publish record can stay
        // stale at PUBLISHING after a booking is cancelled, so gate on the booking's own slot status
        // (CANCELLED bookings drop out) rather than relying on the publish record alone.
        filter: FilterType.SHIFT_SLOT_STATUS,
        values: ['PUBLISHING'],
        selectionLabel: CommonUtils.getFilterText(FilterType.SHIFT_SLOT_STATUS, ['PUBLISHING']),
      },
      {
        // Default to no date constraint so the tab opens showing ALL available applicants. Empty values
        // are dropped in format-booking-listing-filters (Utils.isEmpty), so no date filter reaches the
        // backend; the user can still set their own range via this filter chip afterward.
        filter: FilterType.DATE_RANGE,
        values: [],
        selectionLabel: CommonUtils.getFilterText(FilterType.DATE_RANGE, []),
      },
      {
        filter: FilterType.SERVICE_TYPE,
        values: [ServiceType.INDIVIDUAL, ServiceType.GROUP],
        selectionLabel: CommonUtils.getFilterText(FilterType.SERVICE_TYPE, [ServiceType.INDIVIDUAL, ServiceType.GROUP]),
      },
      { filter: 'sort', values: [['startDateTime', 'asc']] },
    ],
  },
  ];
}

/** Legacy export for callers that expect a static value. Prefer getDefaultBookingViews() for fresh dates. */
export const defaultBookingViews = getDefaultBookingViews();
